import React, { useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Icon } from '@column/column-ui-kit';
import { type DataTableColumnDef } from '@column/ui';

import { JSONButton } from '../../Events';
import { PopoverFilterEntry, Section } from '~/components';
import { EventDetailModal } from '~/components/Dialog';
import { PaginatedDataTable, getTimestampColumn, useTimeFormat } from '~/components/Table';
import { EventRepository, parseEvent } from '~/repositories';
import { Inner } from '~/styles';
import { EventEvent } from '~/typings/API';
import { formatString } from '~/util';

import { CardTokenProps } from './CardToken';

interface Params {
  id: string;
}

const CARD_TOKEN_EVENT_TYPES = [
  'card_token.created',
  'card_token.activated',
  'card_token.paused',
  'card_token.deactivated',
  'card_token.send_passcode',
  'card_token.pending_activation',
  'card_token.stepping_up',
  'card_token.pending_authentication',
  'card_token.activation_denied',
];

const EventMeta = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: center;
  margin: -4px 0;
  gap: 4px;
`;

const EventType = styled.h4`
  flex: auto;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  margin: 0;
  flex-grow: 0;
  padding: 0;
  text-transform: capitalize;
`;

const EventDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.secondary.blendToBackground(600)};
`;

const Action = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const getCardTokenEventLabel = (eventType: string): string => {
  const parts = eventType.split('.');
  if (parts.length < 2) return formatString(eventType);

  const action = parts[parts.length - 1];
  switch (action) {
    case 'created':
      return 'Token Created';
    case 'activated':
      return 'Token Activated';
    case 'paused':
      return 'Token Paused';
    case 'deactivated':
      return 'Token Deactivated';
    case 'send_passcode':
      return 'Passcode Sent';
    case 'pending_activation':
      return 'Token Pending Activation';
    case 'stepping_up':
      return 'Token Stepping Up';
    case 'pending_authentication':
      return 'Token Pending Authentication';
    case 'activation_denied':
      return 'Token Activation Denied';
    default:
      return formatString(action);
  }
};

export const PageCardTokenEvents: React.FC = () => {
  const { id: cardTokenId } = useParams<keyof Params>() as Params;
  useOutletContext<CardTokenProps>();
  const [isEventDetailModalOpen, setIsEventDetailModalOpen] = useState(false);
  const [selectedEventData, setSelectedEventData] = useState<any>(null);
  const { timeFormat, onTimeFormatChange } = useTimeFormat('card-token-events');

  const handleOpenEventDetail = (data: any) => {
    setSelectedEventData(data);
    setIsEventDetailModalOpen(true);
  };

  const handleFetch = async (params: any) => {
    return EventRepository.getAll({
      ...params,
      objectIds: [cardTokenId],
    }).then((response) => {
      const entries = response.events.map((event: EventEvent) => ({
        ...parseEvent(event),
        action: (
          <Action>
            <JSONButton
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEventDetail(event.data);
              }}
            >
              JSON
            </JSONButton>
          </Action>
        ),
      }));

      return { entries, hasMore: Boolean(response?.hasMore) };
    });
  };

  const filter: PopoverFilterEntry[] = [
    {
      id: 'types',
      label: 'Type',
      type: 'select',
      list: CARD_TOKEN_EVENT_TYPES.map((eventType) => ({
        label: getCardTokenEventLabel(eventType),
        value: eventType,
      })),
    },
    {
      id: 'created',
      label: 'Created',
      type: 'date',
    },
  ];

  const columns: DataTableColumnDef<Record<string, any>>[] = [
    {
      header: 'Event',
      accessor: 'eventType',
      width: 'minmax(200px, auto)',
      cell: ({ value }) => (
        <EventMeta>
          <EventType>{getCardTokenEventLabel(value)}</EventType>
          <EventDetail>
            <span>{value}</span>
          </EventDetail>
        </EventMeta>
      ),
    },
    getTimestampColumn<Record<string, any>>({
      accessor: 'createdAt',
      label: 'Date',
      timeFormat,
      onTimeFormatChange,
      width: 'minmax(140px, 180px)',
    }),
    {
      header: '',
      accessor: 'action',
      width: '80px',
      cell: ({ value }) => value,
    },
  ];

  return (
    <>
      <EventDetailModal
        isOpen={isEventDetailModalOpen}
        onClose={() => setIsEventDetailModalOpen(false)}
        data={selectedEventData}
      />

      <Inner p={0}>
        <Section headline="Events">
          <PaginatedDataTable<Record<string, any>>
            tableId="card-token-events"
            fetch={handleFetch}
            filter={filter}
            empty={{
              icon: <Icon.AnimationSignal />,
              title: 'No events yet',
              description: 'Events will appear here when card token status changes occur.',
            }}
            columns={columns}
            rowClassName="min-h-11 py-1"
          />
        </Section>
      </Inner>
    </>
  );
};
